import application from 'application';
import serverInterface from 'server-interface';

import {
  findIndexByPropertyValue
} from 'utilities/collection';

import {
  applyTraits
} from 'utilities/javascript';
import InitializeOnce from 'utilities/javascript/initialize-once';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import {
  convertUserPropertyValuesToLabelsFormat
} from './utilities';

class CustomerLabelsManager {

  constructor(application) {

    this.initializePromise = null;
    this.customerLabels = [];

    this.application = application;

  }

  async doInitialize() {

    this.registerEventHandlers();

    await this.loadAll();

  }

  tryAddCustomerLabel(customerLabel) {

    const customerLabelIndex = findIndexByPropertyValue(this.customerLabels, '_id', customerLabel._id);

    if (customerLabelIndex !== -1) {
      return;
    }

    this.customerLabels.push(customerLabel);

  }

  tryUpdateCustomerLabel(customerLabel) {

    const customerLabelIndex = findIndexByPropertyValue(this.customerLabels, '_id', customerLabel._id);

    if (customerLabelIndex === -1) {
      return;
    }

    this.customerLabels[customerLabelIndex] = customerLabel;

  }

  tryRemoveCustomerLabel(customerLabelId) {

    const customerLabelIndex = findIndexByPropertyValue(this.customerLabels, '_id', customerLabelId);

    if (customerLabelIndex === -1) {
      return;
    }

    this.customerLabels.splice(customerLabelIndex, 1);

  }

  handleCustomerLabelCreatedEvent(payload) {

    this.tryAddCustomerLabel(payload.conversationLabel);

  }

  handleCustomerLabelUpdatedEvent(payload) {

    this.tryUpdateCustomerLabel(payload.conversationLabel);

  }

  handleCustomerLabelDeletedEvent(payload) {

    this.tryRemoveCustomerLabel(payload.conversationLabelId);

  }

  registerEventHandlers() {

    this.application.eventSystem.internal.on(externalEventCodes.USER_SPACE_CONVERSATION_LABEL_CREATED, this.handleCustomerLabelCreatedEvent.bind(this));
    this.application.eventSystem.internal.on(externalEventCodes.USER_SPACE_CONVERSATION_LABEL_UPDATED, this.handleCustomerLabelUpdatedEvent.bind(this));
    this.application.eventSystem.internal.on(externalEventCodes.USER_SPACE_CONVERSATION_LABEL_DELETED, this.handleCustomerLabelDeletedEvent.bind(this));

  }

  getAll() {

    return this.customerLabels;

  }

  namesToIds(names) {

    const customerLabels = this.customerLabels;
    const ids = [];

    for (let name of names) {

      const index = findIndexByPropertyValue(customerLabels, 'name', name);

      if (index === -1) {
        continue;
      }

      const label = customerLabels[index];

      ids.push(label._id);

    }

    return ids;

  }

  async loadAll() {

    const customerLabels = await serverInterface.startup.users.customerLabels.getCustomerLabelsByQueryOptions({
      paginationOptions: {
        limit: 100
      }
    });

    this.customerLabels = convertUserPropertyValuesToLabelsFormat(customerLabels);

  }

  async create(data) {

    try {

      const customerLabel = await serverInterface.startup.users.customerLabels.createCustomerLabel({
        name: data.name
      });

      const formatted = convertUserPropertyValuesToLabelsFormat([customerLabel])[0];

      this.tryAddCustomerLabel(formatted);

      return formatted;

    } catch (exception) {

      let message = 'Unknown error! Contact administration';

      if (exception.code === 'ValidationError') {

        const error = exception.metadata[0];

        if (error.keyword === 'minLength') {
          message = 'Please enter at least 2 letters';
        } else {
          message = 'Please enter no more than 50 letters';
        }

      }

      // eslint-disable-next-line no-throw-literal
      throw { message };

    }

  }

  handleEditLabelException(exception, value) {

    let message = 'Unknown error! Please try again!!';

    if (exception.code === 'USER_SPACE_CONVERSATION_LABEL_ALREADY_EXISTS') {

      message = `Label: ${value} already exists!`;

    } else if (exception.code === 'ValidationError') {

      const error = exception.metadata[0];

      if (error.keyword === 'minLength') {
        message = 'Please enter valid label name';
      } else if (error.keyword === 'maxLength') {
        message = 'Label name cannot exceed 20 characters';
      }

    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });

  }

  async update(customerLabelId, data) {

    try {

      const payload = {
        value: data.name
      };

      const updated = await serverInterface.startup.users.customerLabels.updateCustomerLabelById(customerLabelId, payload);

      if (updated === true) {
        this.tryUpdateCustomerLabel({
          ...data,
          _id: customerLabelId
        });
      }

      return updated;

    } catch (exception) {

      if (exception.code === 'ValidationError') {

        const error = exception.metadata[0];
        let message = null;

        if (error.keyword === 'minLength') {
          message = 'Please enter valid label name with at least 2 letters';
        } else if (error.keyword === 'maxLength') {
          message = 'Label name cannot exceed 20 characters';
        }

        // eslint-disable-next-line no-throw-literal
        throw { message };

      } else {

        // eslint-disable-next-line no-throw-literal
        throw { message: 'Unknown error. Please contact administration!' };

      }

    }

  }

  async delete(customerLabelId) {

    const deleted = await serverInterface.startup.users.customerLabels.deleteCustomerLabelById(customerLabelId);

    if (deleted === true) {
      this.tryRemoveCustomerLabel(customerLabelId);
    }

    return deleted;
  }

}

export default applyTraits(CustomerLabelsManager, [InitializeOnce]);